import React from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import SnackbarSuccessComponent from "./shared/SnackBarSuccessComponent";

import {
  Box,
  Chip,
  Grid,
  IconButton,
  TextField,
  Typography,
  FormLabel,
  Divider,
  Button,
  Tabs,
  Tab,
  Tooltip,
  MenuItem,
  Switch,
  Alert,
  styled,
  DialogProps,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Link,
  CircularProgress,
} from "@mui/material";
import { RightContainerStyled } from "../../assets/styles/theme";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import SnackbarComponent from "./shared/SnackBarComponent";

import OriginalGrid from "./shared/OriginalDataGrid";

import FileUpload from "./modals/FileUpload";

import JSZip from "jszip";
import { saveAs } from "file-saver";

import * as Yup from "yup";

import {
  getUserRoles,
  FILE_LIST_SCREEN_LABEL,
  FILE_LIST_TABLE_PLACEHOLDER,
  FILE_LIST_TABLE_STORAGE_KEY,
} from "../helpers/constants";

import {
  useDeleteClientFile,
  useChangeFileStatus,
  useGetExperiments,
  useGetClientFiles,
  useGetClientDetails,
  useGetClientExperimentsFiles,
  useUpdateDownloadStatus,
} from "../hooks";

import {
  ManageContainer,
  ManagePlatFormWrapper,
} from "./styles/ManagePlatformsComponent.style";
import { CLIENT_STATUS } from "../helpers/constants";
import DeleteIconSvg from "../../assets/images/icon-delete.svg";
import DeleteIconErrorSvg from "../../assets/images/icon-delete-error.svg";
import AddIcon from "@mui/icons-material/Add";

import DownloadFile from "./modals/DownloadFile";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CloseIcon from "@mui/icons-material/Close";
import DeleteFile from "./modals/DeleteFile";
import PlatformsComponent from "./PlatformsComponent";
import EditIcon from "@mui/icons-material/Edit";

interface TruncatedTextProps {
  text: string;
  maxLength: number;
}

const TruncatedText: React.FC<TruncatedTextProps> = ({ text, maxLength }) => {
  const [truncated, setTruncated] = React.useState<any>(text);

  React.useEffect(() => {
    setTruncated(
      text.length > maxLength ? text.slice(0, maxLength) + "..." : text
    );
  }, [text, maxLength]);

  return (
    <Tooltip arrow title={text}>
      {truncated}
    </Tooltip>
  );
};

export const FileListColumns = [
  {
    value: "fileName",
    label: "File Name",
    numeric: false,
    date: false,
    showFilter: true,
    showColumn: true,
  },
  {
    value: "createdBy",
    label: "Uploaded By",
    numeric: false,
    date: false,
    showFilter: true,
    showColumn: true,
  },
  {
    value: "systemCreatedDateTime",
    label: "Upload Date",
    numeric: false,
    date: true,
    showFilter: true,
    showColumn: true,
  },
  {
    value: "isSensitive",
    label: "File Classification",
    numeric: false,
    date: false,
    showFilter: true,
    showColumn: true,
  },

  {
    value: "action",
    label: "Action",
    numeric: false,
    date: false,
    showFilter: false,
    showColumn: true,
  },
];

export const FileListResearchExpColumns = [
  {
    value: "fileName",
    label: "Title",
    numeric: false,
    date: false,
    showFilter: true,
    showColumn: true,
  },
  {
    value: "createdBy",
    label: "Submitted By",
    numeric: false,
    date: false,
    showFilter: true,
    showColumn: true,
  },
  {
    value: "systemCreatedDateTime",
    label: "Submission Date",
    numeric: false,
    date: true,
    showFilter: true,
    showColumn: true,
  },
  {
    value: "filePath",
    label: "Attachments",
    numeric: false,
    date: false,
    showFilter: true,
    showColumn: true,
  },

  {
    value: "action",
    label: "Action",
    numeric: false,
    date: false,
    showFilter: false,
    showColumn: true,
  },
];

interface ManagePlatformsComponentProps {
  data: any;
  clientHasDataSourceId: any;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const ManagePlatformsComponent: React.FC<ManagePlatformsComponentProps> = ({
  data,
}) => {
  const navigate = useNavigate();

  const [fileUploadData, setFileUploadData] = React.useState([]);
  const [researchExperiment, setResearchExperiment] = React.useState([]);

  const [value, setValue] = React.useState(0);
  const [clientId, setClientId] = React.useState(null);

  const {
    data: getExperimentsData,
    isLoading: isGetExperimentsDataPending,
    error: getExperimentsError,
    refetch,
  } = useGetExperiments(clientId);

  const {
    data: getClientFilesData,
    isLoading: isGetClientFilesDataPending,
    error: getClientFilesError,
  } = useGetClientFiles(data.id);

  const {
    data: getClientExperimentsFilesData,
    isLoading: isGetClientExperimentsFilesDataPending,
    error: getClientExperimentsFilesError,
  } = useGetClientExperimentsFiles(data.id);

  const {
    data: getClientDetails,
    isLoading: isGetClientDetailsPending,
    error: getClientDetailsError,
  } = useGetClientDetails(data.id);

  const {
      mutate: updateFileDownloadStatus,
      isPending: isUpdateFileDownloadStatusPending,
    } = useUpdateDownloadStatus();


  React.useEffect(() => {
    if (getClientFilesData && getClientFilesData?.data) {
      setFileUploadData(getClientFilesData?.data);
    }
  }, [getClientFilesData]);

  React.useEffect(() => {
    if (getClientExperimentsFilesData && getClientExperimentsFilesData?.data) {
      setResearchExperiment(getClientExperimentsFilesData?.data);
    }
  }, [getClientExperimentsFilesData]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const queryClient = useQueryClient();

  const renderParentAccount = (value: any) => {
    console.log("value", value
      ?.map((item: any) => item?.name)
      .join(", ")
      .substring(0, 35) + "...")
    if (
      Array.isArray(value)
    ) {
      let parentAccount: any =
        value
          ?.map((item: any) => item?.name)
          .join(", ")
          .substring(0, 35) + "...";
      return parentAccount;
    }
  };

  const [fullWidth, setFullWidth] = React.useState(true);

  const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiDialogContent-root": {
      padding: theme.spacing(2),
    },
    "& .MuiDialogActions-root": {
      padding: theme.spacing(1),
    },
  }));

  const [open, setOpen] = React.useState(false);

  const handleClose = () => {
    setOpen(false);
  };

  const [openFileUpload, setOpenFileUpload] = React.useState(false);
  const [fileUploadType, setFileUploadType] = React.useState("");

  const [openDeleteModal, setOpenDeleteModal] = React.useState(false);

  const handleClickFileUpload = (type: any) => {
    setOpenFileUpload(true);
    setFileUploadType(type);
  };

  const handlePopUpClose = () => {
    console.log("close handlePopUpClose manage");
    setOpenFileUpload(false);
    setFileUploadType("");
    setActionType("");
  };

  const handleSave = (fileName: any) => {
    console.log("handleSave fileName", fileName);
    const message = isEditMode
      ? "has been updated."
      : fileUploadType == "research"
      ? "submitted."
      : "uploaded.";
    const fileSuccessText = `${fileName} ${message}`;
    setActionType("");
    setOpenFileUpload(false);
    setFileUploadType("");
    setSuccess(true);
    setSuccessMessage(fileSuccessText);
    if (fileUploadType == "research") {
      queryClient.invalidateQueries({ queryKey: ["experiments"] });
    } else {
      queryClient.invalidateQueries({ queryKey: ["files"] });
    }
  };

  const [selectedFileForActions, setSelectedFileForActions] =
    React.useState<any>({});
  const [actionType, setActionType] = React.useState("");
  const [fileStatus, setFileStatus] = React.useState(false);

  const [openDownloadModal, setOpenDownloadModal] = React.useState(false);

  const [error, setError] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");

  const [success, setSuccess] = React.useState(false);
  const [successMessage, setSuccessMessage] = React.useState("");
  const [isEditMode, setIsEditMode] = React.useState(false);

  const { mutate: deleteClientFile, isPending: isDeleteClientFilePending } =
    useDeleteClientFile();

  const { mutate: changeFileStatus, isPending: isChangeFileStatusPending } =
    useChangeFileStatus();

  React.useEffect(() => {
    if (getExperimentsData && getExperimentsData?.data) {
      console.log("getExperimentsData manage");
      setSelectedFileForActions(getExperimentsData?.data[0]);
      setIsEditMode(true);
      setOpenFileUpload(isEditMode);
      setFileUploadType("research");
    }
  }, [!isGetExperimentsDataPending, getExperimentsData?.data]);

  const handelAction = (item: any, type: any, status: any) => {
    console.log("item", item, type, status);

    if (type === "Download") {
      setSelectedFileForActions(item);
      if (item.uploadTypeId === 1) {
        downloadFiles(item.filePath, item);
      } else {
        setOpenDownloadModal(true);
      }
    } else if (type === "Delete" || type === "Status") {
      setSelectedFileForActions(item);
      setOpenDeleteModal(true);
      setActionType(type);
      setFileStatus(status);
    } else if (type === "Edit") {
      // Set action type and related states
      setActionType(type);
      setFileStatus(status);
      setFileUploadType("research");

      // Clear previous form data
      setSelectedFileForActions({});
      setIsEditMode(false);

      // Update the clientId
      setClientId(item?.experimentId);

      // Explicitly refetch instead of relying on queryKey changes
      // Use setTimeout to ensure clientId is updated in the closure
      setTimeout(() => {
        console.log("Explicitly refetching for:", item?.experimentId);
        refetch().then((response: any) => {
          if (response.data?.data?.length > 0) {
            console.log("Fetched fresh data:", response?.data?.data[0]);
            setSelectedFileForActions(response?.data?.data[0]);
            setIsEditMode(true);
          }
        });
      }, 0);
    } else {
      setActionType(type);
      setFileStatus(status);
    }
  };

  const handelActionMain = (item: any, type: any, status: any) => {
    console.log("item", item, type, status);
    setSelectedFileForActions(item);
    setActionType(type);
    setFileStatus(status);
    if (type == "Download") {
      downloadFiles(item.filePath, item);
    } else if (type == "Delete" || type == "Status") {
      setOpenDeleteModal(true);
    } else if (type == "Edit") {
      console.log("clientId", clientId, item.experimentId, getExperimentsData);
      if (clientId && clientId == item?.experimentId) {
        setSelectedFileForActions(getExperimentsData?.data[0]);
        setIsEditMode(true);
      } else {
        setClientId(item?.experimentId);
      }
    }
  };

  const handleDeleteFileCancel = () => {
    setOpenDeleteModal(false);
    setSelectedFileForActions({});
    setActionType("");
    setFileStatus(false);
    setIsEditMode(false);
  };

  const handleDeleteFileConfirm = () => {
    console.log("selectedFileForActions", selectedFileForActions);
    if (actionType == "Delete") {
      let url =
        (selectedFileForActions?.uploadTypeId == 1 &&
          selectedFileForActions?.uploadType) == "File Upload"
          ? "files"
          : "experiments";
      const body = {
        id:
          (selectedFileForActions?.uploadTypeId == 1 &&
            selectedFileForActions?.uploadType) == "File Upload"
            ? selectedFileForActions?.id
            : selectedFileForActions?.experimentId,
        baseurl: url,
      };
      deleteClientFile(body, {
        onSuccess: onDeleteClientFileSuccess,
        onError: onDeleteClientFileError,
      });
    } else if (actionType == "Status") {
      let reqBody = {
        isSensitive: fileStatus,
      };
      const body = {
        id: selectedFileForActions?.id,
        reqBody: reqBody,
      };
      changeFileStatus(body, {
        onSuccess: onDeleteClientFileSuccess,
        onError: onDeleteClientFileError,
      });
    }
  };

  const onDeleteClientFileSuccess = (response: any) => {
    console.log("success", response);
    console.log("fileUploadType", fileUploadType);
    const status = fileStatus ? "Sensitive" : "Non Sensitive";
    const deletedSuccessMessage = `${selectedFileForActions.fileName} has been successfully deleted.`;
    const statusChangeSuccessMessage = `${selectedFileForActions.fileName} has been changed to ${status}`;
    const message =
      actionType == "Delete"
        ? deletedSuccessMessage
        : statusChangeSuccessMessage;

    setOpenDeleteModal(false);
    setSuccess(true);
    console.log("message", message);
    setSuccessMessage(message);
    setSelectedFileForActions({});
    setActionType("");
    setFileStatus(false);
    queryClient.invalidateQueries({ queryKey: ["experiments"] });
    if (fileUploadType == "research") {
      queryClient.invalidateQueries({ queryKey: ["experiments"] });
    } else {
      queryClient.invalidateQueries({ queryKey: ["files"] });
    }
  };

  const onDeleteClientFileError = (error: any) => {
    console.log("error", error);
    setOpenDeleteModal(false);
    setSelectedFileForActions({});
    setActionType("");
    setFileStatus(false);
    setIsEditMode(false);
    setError(true);
    setErrorMessage(error?.data?.message);
  };

  const handleFilePopUpClose = () => {
    console.log("this");
    setOpenDeleteModal(false);
    setOpenFileUpload(false);
    setSelectedFileForActions({});
    setIsEditMode(false);
    setClientId(null);
    setActionType("");
  };

  async function downloadFiles(filePaths: any, item :any) {
    // If only one file is provided, download it directly
    if (filePaths.length === 1) {
      const filePath = filePaths[0];
      try {
        const response = await fetch(filePath);
        if (!response.ok) {
          throw new Error(`Failed to fetch ${filePath}`);
        }
        const blob = await response.blob();
        const filename = filePath.split("/").pop().split("?")[0];
        saveAs(blob, filename); // Download the single file
        console.log(`Downloaded file: ${filename}`);
        updateDownloadStatus(item, [item.id]);
      } catch (error) {
        console.error("Error downloading file:", error);
      }
    }
    // If multiple files are provided, bundle them into a zip
    else if (filePaths.length > 1) {
      const zip = new JSZip();

      // Fetch each file and add it to the zip
      const fetchPromises = filePaths.map(async (filePath: any) => {
        const response = await fetch(filePath);
        if (!response.ok) {
          throw new Error(`Failed to fetch ${filePath}`);
        }
        const blob = await response.blob();
        const filename = filePath.split("/").pop().split("?")[0];
        zip.file(filename, blob);
      });

      try {
        // Wait for all files to be fetched and added to the zip
        await Promise.all(fetchPromises);

        // Generate the zip file
        const content = await zip.generateAsync({ type: "blob" });

        // Trigger the download
        saveAs(content, "download.zip");
        updateDownloadStatus(item, [item.id]);

        console.log("All files have been downloaded and zipped.");
      } catch (error) {
        console.error("Error downloading files:", error);
      }
    } else {
      console.error("No file paths provided.");
    }
  }

  const handleAttachmentsDownload = (item: any) => {
    setOpenDownloadModal(true);
    setSelectedFileForActions(item);
  };

  const handleDownloadFileCancel = () => {
    setOpenDownloadModal(false);
    setSelectedFileForActions({});
    setActionType("");
  };

  const handleSaveFormSubmission = () => {
    console.log("handleSaveFormSubmission manage");
    setIsEditMode(false);
    setOpenFileUpload(false);
    setSelectedFileForActions({});
    setActionType("");
    setClientId(null);
  };

  const fileListObject = (filePaths: any) => {
    const fileList = filePaths.map((filePath: any, index: any) => {
      const filename = filePath.filePath.split("/").pop().split("?")[0];
      return {
        id: filePath.fileId,
        name: filename,
        filePath: filePath.filePath,
      };
    });
    return fileList;
  };

  const hideSuccess = () => {
    setSuccess(false);
    setSuccessMessage("");
  };

  const hideError = () => {
    setError(false);
    setErrorMessage("");
  };

  const handleRedirect = () => {
    navigate(`/${data.prevScreen}`);
  };

  const updateDownloadStatus = (selectedFileForActions: any, ids: any) => {
    let reqBody = {
      uploadType: selectedFileForActions?.uploadTypeId,
      files: ids,
    };
    const body = {
      id: selectedFileForActions?.clientId,
      reqBody: reqBody,
    };
    updateFileDownloadStatus(body, {
      onSuccess: onUpdateFileDownloadStatusSuccess,
      onError: onUpdateFileDownloadStatusError,
    });
  };

  const onUpdateFileDownloadStatusSuccess = (res: any) => {
    setOpenDownloadModal(false);
  };

  const onUpdateFileDownloadStatusError = (err: any) => {
    setOpenDownloadModal(false);
  };

  const handleDownloadFileConfirm = (ids: any) => {
    console.log("ids", ids);
    updateDownloadStatus(selectedFileForActions, ids);
  };

  return (
    <>
      <RightContainerStyled className="managePlatformContainer">
        <ManagePlatFormWrapper>
          <Typography variant="h1" component="h1">
            <IconButton
              aria-label="back "
              size="small"
              className="btnBack"
              onClick={handleRedirect}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            MANAGE DATA SOURCE CONNECTIONS
          </Typography>

          <ManageContainer>
            <Typography variant="h2" component="h2">
              CLIENT INFO
            </Typography>

            {isGetClientDetailsPending ? (
              <Box className="hvCenter">
                <CircularProgress size="1.5rem" sx={{ mt: 1.5, mb: 1 }} />
              </Box>
            ) : (
              <>
                <Grid container spacing={3} className="clientInfoOnlyBox">
                  <Grid xs={4} item>
                    <TextField
                      id=""
                      label="Client Legal Name"
                      defaultValue={
                        getClientDetails?.data?.clientName
                          ? getClientDetails?.data?.clientName
                          : "-"
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                      InputProps={{
                        readOnly: true,
                      }}
                      variant="standard"
                      multiline
                    ></TextField>
                  </Grid>

                  <Grid xs={4} item>
                    <TextField
                      id=""
                      label="Advisr ID"
                      defaultValue={
                        getClientDetails?.data?.advisrId
                          ? getClientDetails?.data?.advisrId
                          : "-"
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                      InputProps={{
                        readOnly: true,
                      }}
                      variant="standard"
                    ></TextField>
                  </Grid>

                  <Grid xs={4} item>
                    <TextField
                      id=""
                      label="TapClicks ID"
                      defaultValue={
                        getClientDetails?.data?.tapClicksId
                          ? getClientDetails?.data?.tapClicksId
                          : "-"
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                      InputProps={{
                        readOnly: true,
                      }}
                      variant="standard"
                    ></TextField>
                  </Grid>
                </Grid>

                <Grid container spacing={3} className="clientInfoOnlyBox">
                  <Grid xs={4} item>
                    <TextField
                      id=""
                      label="Agency Name"
                      defaultValue={
                        getClientDetails?.data?.agencyName
                          ? getClientDetails?.data?.agencyName
                          : "-"
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                      InputProps={{
                        readOnly: true,
                      }}
                      variant="standard"
                      multiline
                    ></TextField>
                  </Grid>

                  <Grid xs={4} item>
                    <Tooltip
                      arrow
                      title={getClientDetails?.data?.parentAccountDetails
                        ?.map((item: any) => item?.name)
                        .join(", ")}
                    >
                      <TextField
                        id=""
                        label="Parent Account"
                        defaultValue={
                          getClientDetails?.data?.parentAccountDetails?.length >
                          0
                            ? renderParentAccount(
                                getClientDetails?.data?.parentAccountDetails
                              )
                            : "-"
                        }
                        InputLabelProps={{
                          shrink: true,
                        }}
                        InputProps={{
                          readOnly: true,
                        }}
                        variant="standard"
                      ></TextField>
                    </Tooltip>
                  </Grid>

                  <Grid xs={4} item className="clientInfoOnlyBox">
                    <FormLabel className="labelStatus">Client Status</FormLabel>
                    <Chip
                      label={
                        CLIENT_STATUS[getClientDetails?.data?.clientStatus]
                      }
                      size="small"
                      variant="filled"
                      className={CLIENT_STATUS[
                        getClientDetails?.data?.clientStatus
                      ]?.toLowerCase()}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={3} className="clientInfoOnlyBox">
                  <Grid xs={4} item>
                    <TextField
                      id=""
                      label="Industry(s)"
                      defaultValue={
                        getClientDetails?.data?.industry
                          ? getClientDetails?.data?.industry
                          : "-"
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                      InputProps={{
                        readOnly: true,
                      }}
                      variant="standard"
                      multiline
                    ></TextField>
                  </Grid>

                  <Grid xs={4} item>
                    <TextField
                      id=""
                      label="Market(s)"
                      defaultValue={
                        getClientDetails?.data?.market
                          ? getClientDetails?.data?.market
                          : "-"
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                      InputProps={{
                        readOnly: true,
                      }}
                      variant="standard"
                    ></TextField>
                  </Grid>
                </Grid>
              </>
            )}

            <Divider sx={{ mb: 2.5, mt: 1 }} />

            <Box sx={{ width: "100%" }}>
              <Box sx={{}}>
                <Tabs
                  value={value}
                  onChange={handleChange}
                  aria-label="platform detail tab"
                >
                  <Tab label="Data Source Connections" {...a11yProps(0)} />
                  <Tab label="Files Uploaded" {...a11yProps(1)} />
                  <Tab label="Research & Experiments" {...a11yProps(2)} />
                </Tabs>
              </Box>

              <CustomTabPanel value={value} index={0} className="tabContainer">
                <PlatformsComponent clientId={data.id} scrollToSectionId={data.clientHasDataSourceId}/>
              </CustomTabPanel>

              <CustomTabPanel value={value} index={1} className="tabContainer">
                <Grid container spacing={0} sx={{ mb: 0 }}>
                  <Grid xs={12} item className="ManagePlaltFormFileUploadTable">
                    <OriginalGrid
                    showUnassignedToggle={false}
                      data={fileUploadData}
                      columns={FileListColumns}
                      onRowClick={(item) => console.log(item)}
                      isGroupingEnabled={false}
                      isExportEnabled={false}
                      defaultSortColumn="systemCreatedDateTime"
                      defaultSortDirection="desc"
                      rowsPerPageOptions={[10, 20, 50, 100]}
                      storageKey={"FileList-upload-Table-Manage-Platform"}
                      loading={false}
                      showFileUploadModal={true}
                      placeholder={FILE_LIST_TABLE_PLACEHOLDER}
                      isFixedColumn={true}
                      exportFileName={"FileList"}
                      isFixedRightColumn={false}
                      handelAction={handelAction}
                      isEditMode={false}
                      editData={{}}
                      handleFilePopUpClose={handleFilePopUpClose}
                      handleAttachmentsDownload={() =>
                        console.log("handleAttachmentsDownload")
                      }
                      fromScreen=""
                      industryOptions={[]}
                      handleSaveFormSubmission={() =>
                        console.log("handleSaveFormSubmission")
                      }
                      uploadType={null}
                    />

                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      className="btn-outlined"
                      sx={{ mt: 3 }}
                      onClick={() => handleClickFileUpload("fileUpload")}
                    >
                      ADD FILES
                    </Button>
                  </Grid>
                </Grid>
              </CustomTabPanel>

              <CustomTabPanel value={value} index={2} className="tabContainer">
                <Grid container spacing={0} sx={{ mb: 0 }}>
                  <Grid
                    xs={12}
                    item
                    className="ManagePlaltFormFileUploadTable ResearchAndExperimentTable"
                  >
                    <OriginalGrid
                      showUnassignedToggle={false}
                      data={researchExperiment}
                      columns={FileListResearchExpColumns}
                      onRowClick={(item) => console.log(item)}
                      isGroupingEnabled={false}
                      isExportEnabled={false}
                      defaultSortColumn="systemCreatedDateTime"
                      defaultSortDirection="desc"
                      rowsPerPageOptions={[10, 20, 50, 100]}
                      storageKey={"FileList-research-Table-Manage-Platform"}
                      loading={false}
                      showFileUploadModal={true}
                      placeholder={FILE_LIST_TABLE_PLACEHOLDER}
                      isFixedColumn={true}
                      exportFileName={"FileList"}
                      isFixedRightColumn={false}
                      handelAction={handelAction}
                      isEditMode={isEditMode}
                      editData={selectedFileForActions}
                      handleFilePopUpClose={handleFilePopUpClose}
                      handleAttachmentsDownload={handleAttachmentsDownload}
                      fromScreen="ManagePlatform"
                      industryOptions={[getClientDetails?.data?.industry]}
                      handleSaveFormSubmission={handleSaveFormSubmission}
                      uploadType={null}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      className="btn-outlined"
                      onClick={() => handleClickFileUpload("research")}
                      sx={{ mt: 2 }}
                    >
                      ADD FORMS
                    </Button>
                  </Grid>
                </Grid>
              </CustomTabPanel>
            </Box>
          </ManageContainer>
        </ManagePlatFormWrapper>
      </RightContainerStyled>

      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="download-attachment-modal"
        open={open}
        fullWidth={fullWidth}
        maxWidth={false}
        sx={{ "& .MuiDialog-paper": { maxWidth: "440px" } }}
        className="digitalModal"
      >
        <DialogContent>
          <Box sx={{ p: "0 10px 0 10px", mt: 1 }}>
            <Typography variant="body1" component="span" className="fs-13">
              {" "}
              Are you sure you want to turn this Data Source to inactive?
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions className="" sx={{ pt: "5px !important" }}>
          <Button variant="outlined" size="small" sx={{ mr: 1 }}>
            CANCEL
          </Button>
          <Button variant="contained" size="small">
            CONFIRM
          </Button>
        </DialogActions>
      </BootstrapDialog>

      {openFileUpload && actionType != "Edit" && (
        <FileUpload
          handleSave={handleSave}
          openFileUpload={openFileUpload}
          uploadType={fileUploadType}
          handlePopUpClose={handlePopUpClose}
          initialFileValues={{
            client: {
              label: getClientDetails?.data?.clientName,
              value: getClientDetails?.data?.id,
            },
          }}
          isEditMode={isEditMode}
          editData={selectedFileForActions}
          industries={[getClientDetails?.data?.industry]}
          fromScreen={"ManagePlatform"}
        />
      )}

      {openDeleteModal && (
        <DeleteFile
          open={openDeleteModal}
          handleCancel={handleDeleteFileCancel}
          handleConfirm={handleDeleteFileConfirm}
          subject={actionType}
          status={fileStatus}
          fileName={selectedFileForActions?.fileName}
          isLoading={isDeleteClientFilePending || isChangeFileStatusPending}
        />
      )}

      {openDownloadModal && (
        <DownloadFile
          open={openDownloadModal}
          handleCancel={handleDownloadFileCancel}
          handleConfirm={handleDownloadFileConfirm}
          fileList={fileListObject(selectedFileForActions?.fileDetailsList)}
        />
      )}
      {error && (
        <SnackbarComponent
          error={error}
          errorMessage={errorMessage}
          hideError={hideError}
        />
      )}
      {success && (
        <SnackbarSuccessComponent
          success={success}
          successMessage={successMessage}
          hideSuccess={hideSuccess}
        />
      )}
    </>
  );
};

export default ManagePlatformsComponent;
